import type { AdLockReason } from '../config/ads';

const locks = new Map<AdLockReason, number>();
const listeners = new Set<() => void>();
let lastUnlockAt = 0;
const scriptPromises = new Map<string, Promise<boolean>>();

const notify = () => listeners.forEach((listener) => listener());

export function lockAdvertising(reason: AdLockReason) {
  locks.set(reason, (locks.get(reason) ?? 0) + 1);
  notify();
}

export function unlockAdvertising(reason: AdLockReason) {
  const count = locks.get(reason) ?? 0;
  if (!count) return;
  if (count > 1) locks.set(reason, count - 1);
  else locks.delete(reason);
  lastUnlockAt = Date.now();
  notify();
}

export const getAdvertisingLockState = () => ({ reasons: [...locks.keys()], lastUnlockAt });

export function subscribeAdvertisingLocks(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function loadMonetagScript(options: {
  id: string;
  zoneId: string;
  src: string;
  timeoutMs?: number;
}): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);
  const existing = document.getElementById(options.id) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === 'true') return Promise.resolve(true);
  const pending = scriptPromises.get(options.id);
  if (pending) return pending;
  const script = existing ?? document.createElement('script');
  script.id = options.id;
  script.async = true;
  script.dataset.zone = options.zoneId;
  script.src = options.src;
  const promise = new Promise<boolean>((resolve) => {
    const timeout = window.setTimeout(() => resolve(false), options.timeoutMs ?? 8000);
    script.addEventListener(
      'load',
      () => {
        window.clearTimeout(timeout);
        script.dataset.loaded = 'true';
        resolve(true);
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => {
        window.clearTimeout(timeout);
        resolve(false);
      },
      { once: true },
    );
    if (!existing) document.body.appendChild(script);
  });
  scriptPromises.set(options.id, promise);
  return promise;
}

export function resetMonetagForTests() {
  locks.clear();
  lastUnlockAt = 0;
  scriptPromises.clear();
}
